import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Download, GitBranch, Clock } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const when = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

export default function ModuleVersions({ moduleKey, item, onRefresh }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [nota, setNota] = useState("");
  const versions = [...(item.versions || [])].sort((a, b) => b.n - a.n);
  const current = versions[0];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("nota", nota);
    try {
      const { data } = await api.post(`/mod/${moduleKey}/items/${item.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Versión v${data.n} guardada`);
      setNota("");
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al subir versión"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-3" data-testid="module-versions">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 overline text-navy/40"><GitBranch className="w-3.5 h-3.5" /> Control de versiones</div>
        {current && <span className="badge badge-muted text-xs" data-testid="version-current">Versión actual: v{current.n}</span>}
      </div>

      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="version-file-input" />
      <div className="space-y-2">
        <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota de cambios (opcional)…"
          className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm" data-testid="version-note-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn btn-primary" data-testid="version-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir nueva versión
        </button>
        <p className="text-[11px] text-navy/35">El sistema numera cada versión automáticamente (v1, v2, v3…) y conserva todo el historial. Vincula el documento de Drive con “Vincular”.</p>
      </div>

      {versions.length === 0 ? (
        <div className="text-xs text-navy/35">Aún no hay versiones. Sube el primer archivo para iniciar el historial.</div>
      ) : (
        <div className="space-y-1.5">
          {versions.map((v) => (
            <div key={v.n} className="flex items-center justify-between gap-2 border border-navy/10 rounded-lg px-3 py-2" data-testid={`version-${v.n}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-violet shrink-0">v{v.n}</span>
                  <span className="text-sm text-navy/85 truncate">{v.name}</span>
                  <span className="text-navy/30 text-xs shrink-0">{kb(v.size || 0)}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-navy/40 mt-0.5">
                  <Clock className="w-3 h-3" /> {when(v.at)} · {v.by_name || "—"}{v.nota ? ` · ${v.nota}` : ""}
                </div>
              </div>
              <a href={mediaUrl(v.url)} target="_blank" rel="noreferrer" className="text-violet hover:text-navy shrink-0" title="Descargar" data-testid={`version-download-${v.n}`}><Download className="w-4 h-4" /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
